import { useState } from 'react'
import { BlockComponents } from '@winnin/winnin_ui.components.block-components'
import { Button } from '@winnin/winnin_ui.components.button'
import { InputWithLabel } from '@winnin/winnin_ui.components.input-with-label'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { T, useTranslate } from 'lib/tolgee'
import { TOLGEE_GLOBALS } from 'utils'

import './styles.scss'

const TOLGEE_PREFFIX = 'NewFolderInput'

const MIN_INPUT_LENGTH = 3
const MAX_INPUT_LENGTH = 36

/**
 * @param {Object} props
 * @param {(folderName: string) => void} props.onCreate
 * @param {boolean} [props.disabled]
 * @param {string} [props.disabledMessage]
 * @param {number} [props.foldersLimit]
 * @param {number} [props.totalFolders]
 */
export const NewFolderInput = props => {
  const { onCreate, disabled, disabledMessage, foldersLimit, totalFolders } = props

  const t = useTranslate()
  const { createToast } = useToast()

  const [isOpen, setIsOpen] = useState(false)
  const [isBlocked, setIsBlocked] = useState(true)

  const handleToggleIsOpen = () => {
    setIsOpen(prevIsOpen => !prevIsOpen)
  }

  const handleChange = event => {
    const value = event.target.value
    const shouldBlockFolderCreation = String(value).length < MIN_INPUT_LENGTH || String(value).length > MAX_INPUT_LENGTH
    setIsBlocked(shouldBlockFolderCreation)
  }

  const handleOnCreate = event => {
    event.preventDefault()

    const formData = new FormData(event.target)
    const { folderName } = Object.fromEntries(formData)

    if (folderName.length < 3) {
      createToast({
        content: t({
          defaultValue: 'The folder name should have at least 3 characters',
          key: `${TOLGEE_PREFFIX}-folderNameLengthError`,
        }),
      })
      return
    }

    onCreate(folderName ?? '')
    event?.target?.reset()
    setIsOpen(false)
    setIsBlocked(true)
  }

  return (
    <form className="NewFolderInput" onSubmit={handleOnCreate}>
      {isOpen ? (
        <>
          <InputWithLabel
            autoFocus
            name="folderName"
            placeholder={t({
              key: `${TOLGEE_PREFFIX}-placeholder`,
              defaultValue: 'Digite um nome para a sua nova pasta',
            })}
            maxLength={MAX_INPUT_LENGTH}
            onChange={handleChange}
          />
          <div className="NewFolderInput-actions-wrapper">
            <Button variation="faded" appearance="link" style={{ fontSize: '12px' }} onClick={handleToggleIsOpen}>
              <T keyName={`${TOLGEE_PREFFIX}-cancel-button`}>Cancelar</T>
            </Button>
            <BlockComponents
              blockMessage={t({
                key: `${TOLGEE_PREFFIX}-createFolder-block-message`,
                parameters: {
                  minValue: MIN_INPUT_LENGTH,
                },
                defaultValue:
                  'Para prosseguir com a criação da pasta, o campo de nome deve ter no mínimo {minValue} caracteres.',
              })}
              isClickBlocked={isBlocked}
              tooltipPositionClassname="tooltippedNw"
            >
              <Button
                variation="primary"
                appearance="link"
                type="submit"
                disabled={isBlocked}
                style={{ fontSize: '12px' }}
              >
                <T keyName={`${TOLGEE_PREFFIX}-create-button`}>Criar</T>
              </Button>
            </BlockComponents>
          </div>
        </>
      ) : (
        <div className="NewFolderInput-button-wrapper">
          <BlockComponents
            blockMessage={disabledMessage}
            tooltipPositionClassname="tooltippedNe"
            className="NewFolderInput-reachedLimit-container"
            isClickBlocked={disabled}
          >
            <Button
              variation="faded"
              appearance="link"
              icon="add"
              iconSize={16}
              className="NewFolderInput-button"
              onClick={handleToggleIsOpen}
              disabled={disabled}
            >
              <T keyName={`${TOLGEE_PREFFIX}-open-button`}>Nova pasta</T>
            </Button>
          </BlockComponents>
          {!!foldersLimit && (
            <div className="NewFolderInput-limit">
              {t({
                ...TOLGEE_GLOBALS.foldersLimitStatus,
                parameters: {
                  foldersLimit,
                  totalFolders,
                },
              })}
            </div>
          )}
        </div>
      )}
    </form>
  )
}
